import { CSSProperties } from 'react'
import { useLocalObservable } from 'mobx-react-lite'
import { globalStore } from '@/common'
import { Container } from '@/utils/sparrowContainer'
import moment from 'moment'
import useDelivery, {
  CommentInfo,
  DeliveryItem,
  OperationInfo,
  ProjectIndex,
  SubmitDeliveryItem
} from '../hooks/useDelivery'
import {UserInfo} from '../hooks/useUser'
import useFormatData from '../hooks/useFormatData'

type GlobalModel = {
  currentDate?: string
  deliveryList: DeliveryItem[]
  projectList: ProjectIndex[]
  sortBy:
    | 'businessLineId'
    | 'morningMeetingId'
    | 'taskTypeId'
    | 'taskStatusId'
    | 'participant'
  sortType: 'asc' | 'desc'
  userInfos: UserInfo[]
  userRecord: Record<string, UserInfo>
  participantRecord: Record<
    string,
    { style: CSSProperties; data: DeliveryItem }[]
  >
  commentListMap: Record<string, CommentInfo[]>
  currOperationInfos: OperationInfo[]
}

type DeliveryController = GlobalModel & {
  init: () => void
  handleRefresh: () => void
  // handleSearchUser: (name: string) => Promise<UserInfo[]>
  handleDateChange: (date: moment.Moment) => void
  sortDeliveryList: (
    sortType:
      | 'businessLineId'
      | 'morningMeetingId'
      | 'taskTypeId'
      | 'taskStatusId'
      | 'participant',
    sortBy: 'desc' | 'asc' | ''
  ) => void
  handleSortDeliveryList: (
    src: DeliveryItem[],
    sortType:
      | 'businessLineId'
      | 'morningMeetingId'
      | 'taskTypeId'
      | 'taskStatusId'
      | 'participant',
    sortBy: 'desc' | 'asc' | ''
  ) => DeliveryItem[]
  handleCreateDeliveryItem: (payload: SubmitDeliveryItem) => Promise<boolean>
  handleUpdateDeliveryItems: (payload: {
    deliveryWeekTaskList: (SubmitDeliveryItem & { id: number })[]
  }) => Promise<boolean>
  handleDeleteDeliveryItem: (id: number) => Promise<boolean>
  handleCreateDeliveryConfigType: (
    type: string | 'meeting' | 'bizType' | 'userType' | 'status' | 'type' | '',
    name: string,
    orderNum: number
  ) => Promise<boolean>
  handleUpdateDeliveryConfigType: (
    type: string | 'meeting' | 'bizType' | 'userType' | 'status' | 'type' | '',
    id: number,
    name: string,
    orderNum: number
  ) => Promise<boolean>
  handleDeleteDeliveryConfigType: (
    type: string | 'meeting' | 'bizType' | 'userType' | 'status' | 'type' | '',
    id: number
  ) => Promise<boolean>
  handleQueryCommentListByTargetIds: (ids: string[]) => void
  handleCreateComment: (payload: {
    commentId?: string
    content: string
    extraInfo?: string
    fromUserId?: string
    replyCommentId?: string
    targetId: string
    targetType: string | 'MOMENT' | 'SPARROW'
    toUserId: string
  }) => Promise<boolean>
  handleDeleteComment: (payload: string) => Promise<boolean>
  refreshCommentList: () => void
  handleQueryOperationListById: (id: number) => Promise<void>
}

/**
 * 1: 晨会
 * 2: 业务线
 * 3: 用户角色
 * 4: 任务状态
 * 5: 任务类型
 */
export const configTypeEnum = {
  meeting: 1,
  project: 2,
  userType: 3,
  status: 4,
  type: 5
}

export function getConfigTypeName(typeNumber) {
  for (let key in configTypeEnum) {
    if (configTypeEnum[key] === typeNumber) {
      return key;
    }
  }
  return '-1';
}

function useDeliveryContext(initState: Record<string, string>): DeliveryController {
  const {
    getDeliveryList,
    getAllUserList,
    createDeliveryItem,
    createDeliveryConfigType,
    updateDeliveryConfigType,
    deleteDeliveryConfigType,
    updateDeliveryItems,
    deleteDeliveryItem,
    getCommentListByTargetIds,
    createComment,
    deleteComment,
    getOperationLogById
  } = useDelivery()
  const { arrayToObject, stringToColor } = useFormatData()
  const store = useLocalObservable<GlobalModel>(() => ({
    currentDate: moment().isoWeekday(5).format('YYYY-MM-DD 00:00:00'),
    deliveryList: [],
    projectList:[],
    sortBy: 'taskStatusId',
    sortType: 'desc',
    userInfos: [],
    userRecord: {},
    participantRecord: {},
    commentListMap: {},
    currOperationInfos: []
  }))

  const init = (): void => {
    handleQueryDeliveryList(store.currentDate)

    getAllUserList().then((res) => {
      if (!res) {
        return
      }
      store.userInfos = res
      store.userRecord = arrayToObject<UserInfo>(
        res.map((u) => ({ ...u, id: String(u.id) })),
        'id'
      )
    })
    void globalStore.loadEnumConfigsByAll()
  }

  const convertParticipantRecord = (dataSource: DeliveryItem[] = []): void => {
    if (!dataSource || dataSource.length <= 0) {
      store.participantRecord = {}
      return
    }

    const participantRecord = {}

    dataSource.forEach((item) => {
      item.userList.forEach((userInfoItem) => {
        if (!participantRecord[userInfoItem.userId]) {
          participantRecord[userInfoItem.userId] = []
        }

        participantRecord[userInfoItem.userId].push({
          style: stringToColor(item.taskName),
          data: item
        })
      })
    })

    store.participantRecord = participantRecord
  }

  const handleRefresh = (): void => {
    handleQueryDeliveryList(store.currentDate)
  }

  /**
   *
   * @param finishDate
   */
  const handleQueryDeliveryList = (finishDate?: string) => {
    getDeliveryList({ finishDate }).then((res) => {
      const targetIds = (res || []).map((item) => String(item.id))

    
      
      handleQueryCommentListByTargetIds(targetIds)
      convertParticipantRecord(res)
      store.deliveryList = handleSortDeliveryList(
        res || [],
        store.sortBy,
        store.sortType
      )

      // 提取 projectId 和 projectName，并去重 
      store.projectList = Array.from(
        new Set(
          store.deliveryList.map(item => JSON.stringify({ projectId: item.projectId, projectName: item.projectName }))
        )
      ).map(json => JSON.parse(json));
    })
  }

  // const handleSearchUser = (name: string): Promise<UserInfo[]> => {
  //   return getUserListByName({ name })
  // }

  const handleDateChange = (date?: moment.Moment): void => {
    if (date && date.isoWeekday() !== 5) {
      return
    }
    if (date) {
      store.currentDate = date.format('YYYY-MM-DD 00:00:00')
    } else {
      store.currentDate = undefined
    }

    handleQueryDeliveryList(store.currentDate || null)
  }

  const handleProjectChange = (projectId:number): void => {
     // 修改交付所属的项目。
  }

  const handleCreateDeliveryConfigType = (
    type: 'meeting' | 'type' | 'bizType' | 'status' | 'userType' | '',
    value: string,
    orderNum: number
  ): Promise<boolean> => {
    if(type === ''){
      return;
    }
    return createDeliveryConfigType({
      type: configTypeEnum[type],
      name: value,
      orderNum
    }).then((res) => {
      if (res) {
        void globalStore.loadEnumConfigsByType(configTypeEnum[type])
      }
      return res
    })
  } 


  const handleUpdateDeliveryConfigType = (
    type: 'meeting' | 'type' | 'bizType' | 'status' | 'userType' | '',
    id: number,
    value: string,
    orderNum: number
  ): Promise<boolean> => {
    if(type === ''){
      return ;
    }
    return updateDeliveryConfigType({
      type: configTypeEnum[type],
      id,
      name: value,
      orderNum
    }).then((res) => {
      if (res) {
        void globalStore.loadEnumConfigsByType(configTypeEnum[type])
      }
      return res
    })
  }

  const handleDeleteDeliveryConfigType = (
    type: 'meeting' | 'type' | 'bizType' | 'status' | 'userType' | '',
    id: number
  ): Promise<boolean> => {
    return deleteDeliveryConfigType({
      type: configTypeEnum[type],
      id
    }).then((res) => {
      if (res) {
        void globalStore.loadEnumConfigsByType(configTypeEnum[type])
      }
      return res
    })
  }

  const handleCreateDeliveryItem = (payload: SubmitDeliveryItem) => {
    return createDeliveryItem(payload).then((res) => {
      if (res) {
        handleQueryDeliveryList(store.currentDate)
      }

      return res
    })
  }

  const handleUpdateDeliveryItems = (payload): Promise<boolean> => {
    return updateDeliveryItems(payload).then((res) => {
      if (res) {
        handleQueryDeliveryList(store.currentDate)
      }

      return res
    })
  }

  const handleDeleteDeliveryItem = (id: number): Promise<boolean> => {
    return deleteDeliveryItem({ id }).then((res) => {
      if (res) {
        handleQueryDeliveryList(store.currentDate)
      }

      return res
    })
  }

  const refreshCommentList = () => {
    const targetIds = store.deliveryList.map((item) => String(item.id))
    handleQueryCommentListByTargetIds(targetIds)
  }

  const handleQueryCommentListByTargetIds = (ids: string[]) => {
    getCommentListByTargetIds(ids).then((res) => {
      if (res && res.length > 0) {
        const commentListMap: Record<string, CommentInfo[]> = {}

        res.forEach((item) => {
          if (!commentListMap[item.targetId]) {
            commentListMap[item.targetId] = []
          }

          commentListMap[item.targetId].push(item)
        })

        // console.log(commentListMap)

        store.commentListMap = { ...commentListMap }
      }
    })
  }

  const handleQueryOperationListById = (id: number): Promise<void> => {
    store.currOperationInfos = []
    getOperationLogById({ taskId: id }).then((res) => {
      store.currOperationInfos = [...(res || [])]
    })
    return;
  }

  const handleCreateComment = (payload): Promise<boolean> => {
    return createComment(payload).then((res) => {
      console.log(res)

      return res
    })
  }

  const handleDeleteComment = (payload): Promise<boolean> => {
    return deleteComment(payload).then((res) => {
      console.log(res)

      return res
    })
  }

  const sortDeliveryList = (
    sortBy:
      | 'businessLineId'
      | 'morningMeetingId'
      | 'taskTypeId'
      | 'taskStatusId'
      | 'participant',
    sortType: 'desc' | 'asc'
  ): void => {
    store.sortType = sortType
    store.sortBy = sortBy
    store.deliveryList = handleSortDeliveryList(
      store.deliveryList,
      sortBy,
      sortType
    )
  }

  const handleSortDeliveryList = (
    src: DeliveryItem[],
    sortBy:
      | 'businessLineId'
      | 'morningMeetingId'
      | 'taskTypeId'
      | 'taskStatusId'
      | 'participant',
    sortType: 'desc' | 'asc'
  ): DeliveryItem[] => {
    const localSrc = [...src]

    return localSrc.sort((a, b) => {
      let result = 0

      switch (sortBy) {
        case 'businessLineId':
        case 'taskTypeId':
        case 'taskStatusId':
        case 'morningMeetingId':
          if (sortType === 'desc') {
            result = b[sortBy] - a[sortBy] > 0 ? 1 : -1
          } else if (sortType === 'asc') {
            result = b[sortBy] - a[sortBy] > 0 ? -1 : 1
          }

          break
        case 'participant':
          if (sortType === 'desc') {
            result =
              b.userList.length - a.userList.length === 0
                ? 0
                : b.userList.length - a.userList.length > 0
                ? 1
                : -1
          } else if (sortType === 'asc') {
            result =
              b.userList.length - a.userList.length === 0
                ? 0
                : b.userList.length - a.userList.length > 0
                ? -1
                : 1
          }

          break
      }

      return result
    })
  }

  return Object.assign(store, {
    init,
    // handleSearchUser,
    handleDateChange,
    sortDeliveryList,
    handleSortDeliveryList,
    handleCreateDeliveryItem,
    handleUpdateDeliveryItems,
    handleDeleteDeliveryItem,
    handleCreateDeliveryConfigType,
    handleUpdateDeliveryConfigType,
    handleDeleteDeliveryConfigType,
    handleRefresh,
    handleQueryCommentListByTargetIds,
    handleCreateComment,
    handleDeleteComment,
    refreshCommentList,
    handleQueryOperationListById
  })
}

export default Container.createContainer<DeliveryController, Record<string, string>>(
  useDeliveryContext
)
